
import React from 'react';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { BookOpen, Target, Wrench, MessageSquare, Users, CheckCircle } from 'lucide-react';

const CourseContentSection = () => {
  const { isMobile } = useBreakpoint();

  const modules = [
    {
      number: 1,
      title: "The Easiest Upsell You'll Ever Make",
      description: "Discover the massive opportunity hiding in every business owner's wallet and why they're practically begging for someone to solve this problem",
      icon: Target,
      color: "from-[#003a70] to-[#003a70]/80",
      accentColor: "from-[#FEE6D8] to-[#FEE6D8]/80"
    },
    {
      number: 2,
      title: "The Anatomy of an AI-Powered Business Card",
      description: "Understand the core components that create genuine \"wow\" moments and make prospects immediately want what you're showing them",
      icon: BookOpen,
      color: "from-[#003a70] to-[#003a70]/80",
      accentColor: "from-[#FEE6D8]/80 to-[#FEE6D8]/60"
    },
    {
      number: 3,
      title: "Your Secret Weapon - Building a Demo Card in 15 Minutes",
      description: "Get extensive over-the-shoulder training with AskSpot.ai's drag-and-drop system so you can create AI-powered business cards rapidly and effortlessly",
      icon: Wrench,
      color: "from-[#003a70] to-[#003a70]/80",
      accentColor: "from-[#FEE6D8]/70 to-[#FEE6D8]/90"
    },
    {
      number: 4,
      title: "The \"Non-Sales\" Sales Process",
      description: "Learn simple pricing models and the 3-Step \"Wow\" Demo process that gets prospects to ask YOU for the sale",
      icon: MessageSquare,
      color: "from-[#003a70] to-[#003a70]/80",
      accentColor: "from-[#FEE6D8]/60 to-[#FEE6D8]"
    },
    {
      number: 5,
      title: "The \"Show, Don't Tell\" Marketing Plan",
      description: "Identify target-rich networking environments where you can naturally demonstrate your card and turn every business conversation into a potential sale",
      icon: Users,
      color: "from-[#003a70] to-[#003a70]/80",
      accentColor: "from-[#FEE6D8]/90 to-[#FEE6D8]/70"
    },
    {
      number: 6,
      title: "Your Action Plan & Next Steps",
      description: "Master the conversation script that creates curiosity and gets prospects asking how they can get their own AI business card",
      icon: CheckCircle,
      color: "from-[#003a70] to-[#003a70]/80",
      accentColor: "from-[#FEE6D8] to-[#FEE6D8]/50"
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-[#FEE6D8]/20 via-white to-[#FEE6D8]/30 relative overflow-hidden">
      {/* Enhanced background decoration with accent color */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/4 left-1/6 w-96 h-96 bg-[#FEE6D8]/40 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 right-1/6 w-80 h-80 bg-[#003a70]/10 rounded-full blur-3xl"></div>
        <div className="absolute top-2/3 left-1/3 w-64 h-64 bg-gradient-to-r from-[#FEE6D8]/30 to-[#003a70]/10 rounded-full blur-2xl"></div>
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-5xl">
        <div className="space-y-12">
          
          {/* Enhanced Section Header with accent color */}
          <div className="text-center space-y-6">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#FEE6D8] to-[#FEE6D8]/80 rounded-full shadow-lg border-2 border-[#FEE6D8]/50">
              <BookOpen className="text-[#003a70]" size={24} />
              <span className="text-[#003a70] font-semibold text-lg">Complete Training System</span>
            </div>
            
            <h2 className={cn(
              "font-bold text-[#003a70] leading-tight",
              isMobile ? "text-3xl" : "text-4xl lg:text-5xl"
            )}>
              Here's What You Get Inside{" "}
              <span className="bg-gradient-to-r from-[#003a70] to-[#FEE6D8] bg-clip-text text-transparent">
                The Genius Card Playbook
              </span>
            </h2>
            
            <div className="w-24 h-1 bg-gradient-to-r from-[#FEE6D8] via-[#003a70] to-[#FEE6D8] mx-auto rounded-full shadow-sm"></div>
          </div>

          {/* Enhanced Modules Stack with accent color integration */}
          <div className="space-y-6">
            {modules.map((module, index) => (
              <div
                key={module.number}
                className={cn(
                  "group relative bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border-2 overflow-hidden",
                  "transform hover:-translate-y-1",
                  index % 2 === 0 ? "border-[#FEE6D8]/60 hover:border-[#FEE6D8]" : "border-gray-100 hover:border-[#FEE6D8]/60",
                  isMobile ? "p-6" : "p-8"
                )}
              >
                {/* Enhanced gradient accent bar with alternating colors */}
                <div className={cn(
                  "absolute top-0 left-0 right-0 h-2 bg-gradient-to-r",
                  index % 2 === 0 ? module.accentColor : module.color
                )}></div>
                
                {/* Side accent strip */}
                <div className={cn(
                  "absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b",
                  index % 2 === 0 ? module.accentColor : "from-[#FEE6D8]/60 to-[#FEE6D8]/30"
                )}></div>
                
                <div className="flex items-start gap-6 relative">
                  {/* Enhanced Module Icon & Number with accent color */}
                  <div className="flex-shrink-0">
                    <div className={cn(
                      "relative w-16 h-16 rounded-xl shadow-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300 border-2",
                      index % 2 === 0 
                        ? "bg-gradient-to-br from-[#FEE6D8] to-[#FEE6D8]/80 border-[#FEE6D8]/50" 
                        : "bg-gradient-to-br from-[#003a70] to-[#003a70]/80 border-[#003a70]/30"
                    )}>
                      <module.icon 
                        className={index % 2 === 0 ? "text-[#003a70]" : "text-white"} 
                        size={24} 
                      />
                      <div className={cn(
                        "absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-lg border-2 border-white",
                        index % 2 === 0 
                          ? "bg-[#003a70] text-[#FEE6D8]" 
                          : "bg-[#FEE6D8] text-[#003a70]"
                      )}>
                        {module.number}
                      </div>
                    </div>
                  </div>
                  
                  {/* Module Content with enhanced accent highlights */}
                  <div className="flex-grow space-y-3">
                    <h3 className={cn(
                      "font-bold text-gray-900 group-hover:text-[#003a70] transition-colors",
                      isMobile ? "text-xl" : "text-2xl"
                    )}>
                      <span className={cn(
                        "px-2 py-1 rounded-md mr-2 text-sm font-semibold",
                        index % 2 === 0 
                          ? "bg-[#FEE6D8]/40 text-[#003a70]" 
                          : "bg-[#003a70]/10 text-[#003a70]"
                      )}>
                        Module {module.number}:
                      </span>
                      {module.title}
                    </h3>
                    
                    <p className={cn(
                      "text-gray-700 leading-relaxed",
                      isMobile ? "text-base" : "text-lg"
                    )}>
                      {module.description}
                    </p>
                  </div>
                  
                  {/* Enhanced Hover Arrow with accent color */}
                  <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center shadow-md border-2 border-white",
                      index % 2 === 0 
                        ? "bg-gradient-to-br from-[#FEE6D8] to-[#FEE6D8]/80" 
                        : "bg-gradient-to-br from-[#FEE6D8]/60 to-[#FEE6D8]"
                    )}>
                      <svg
                        className="w-5 h-5 text-[#003a70]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Enhanced Bottom CTA Teaser with prominent accent color */}
          <div className="text-center bg-gradient-to-br from-[#FEE6D8]/60 via-[#FEE6D8]/40 to-[#FEE6D8]/70 rounded-2xl p-8 md:p-10 border-2 border-[#FEE6D8] shadow-xl relative overflow-hidden">
            {/* Decorative accent elements */}
            <div className="absolute top-4 right-4 w-16 h-16 bg-[#FEE6D8]/30 rounded-full blur-xl"></div>
            <div className="absolute bottom-4 left-4 w-12 h-12 bg-[#003a70]/10 rounded-full blur-lg"></div>
            
            <div className="relative">
              <p className={cn(
                "text-gray-800 leading-relaxed mb-4",
                isMobile ? "text-lg" : "text-xl"
              )}>
                <strong className="text-[#003a70] bg-[#FEE6D8]/50 px-2 py-1 rounded-md">This isn't just theory.</strong> Every module includes real-world examples, actionable templates, and step-by-step implementation guides.
              </p>
              
              <p className={cn(
                "text-[#003a70] font-bold bg-white/60 px-4 py-2 rounded-xl inline-block shadow-sm border border-[#FEE6D8]",
                isMobile ? "text-lg" : "text-xl"
              )}>
                You'll be ready to start generating revenue within days, not months.
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default CourseContentSection;
